<template>
  <BaseLayout title="Course Materials">
    <!-- Main Content (Center) -->
    <div class="container my-4">
      <h1 class="display-4 text-center">Course Materials</h1>
      <p class="text-center text-muted">
        Uploaded documents are chunked, embedded and stored so the AI chat can cite them.
        New materials start <strong>hidden</strong> from students — toggle
        “Visible to students” to let students see, download, and get chatbot answers from a file.
      </p>

      <!-- Semester filter -->
      <div class="row justify-content-center mb-3">
        <div class="col-sm-6 col-md-4">
          <label for="semesterFilter" class="form-label">Semester</label>
          <select id="semesterFilter" class="form-select" v-model="selectedSemesterId" @change="fetchDocuments">
            <option :value="null">All semesters</option>
            <optgroup label="Active">
              <option v-for="s in activeSemesters" :key="s.id" :value="s.id">{{ s.name }}</option>
            </optgroup>
            <optgroup v-if="archivedSemesters.length" label="Archived">
              <option v-for="s in archivedSemesters" :key="s.id" :value="s.id">{{ s.name }}</option>
            </optgroup>
          </select>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center my-4">
        <div class="spinner-border" role="status">
          <span class="sr-only">&nbsp;&nbsp;Loading...</span>
        </div>
      </div>
      <div v-else>
        <div v-if="courseDocuments.length === 0" class="alert alert-info text-center">
          No course materials for this selection.
          <button @click="fetchDocuments" class="btn btn-primary">
            <i class="bi bi-arrow-clockwise me-2"></i>Refresh
          </button>
        </div>
        <div v-else class="card">
          <div class="card-header">Course Materials</div>
          <ul class="list-group list-group-flush">
            <li v-for="doc in courseDocuments" :key="doc.id" class="list-group-item">
              <div class="d-flex justify-content-between align-items-start flex-wrap">
                <div class="me-3">
                  <span class="badge bg-secondary me-2 text-uppercase">{{ doc.fileType || 'file' }}</span>
                  <strong>{{ doc.file_name }}</strong>
                  <span v-if="doc.isIndexed" class="badge bg-success ms-2">
                    Indexed · {{ doc.passage_count }} chunks
                  </span>
                  <span v-else-if="doc.isIndexing" class="badge bg-info text-dark ms-2">
                    <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    Indexing…
                  </span>
                  <span v-else class="badge bg-warning text-dark ms-2">
                    ⚠ Not indexed — no text extracted
                  </span>
                  <span v-if="doc.is_available" class="badge bg-success ms-2">
                    <i class="bi bi-eye"></i> Visible
                  </span>
                  <span v-else class="badge bg-secondary ms-2">
                    <i class="bi bi-eye-slash"></i> Hidden
                  </span>
                  <div class="small text-muted mt-1">
                    {{ formatFileSize(doc.file_size) }}
                    <span v-if="doc.isIndexed"> · {{ doc.char_count }} chars indexed</span>
                    · Uploaded {{ formatDate(doc.created_at) }}
                    <span v-if="doc.teacher_uco"> by UČO {{ doc.teacher_uco }}</span>
                    · {{ semesterName(doc.semester_id) }}
                  </div>
                </div>
                <div class="d-flex flex-column align-items-end mt-1">
                  <div class="form-check form-switch mb-1"
                       title="When off, students can't see or download this file — and the study chatbot won't use it to answer them.">
                    <input class="form-check-input" type="checkbox" role="switch"
                           :id="`visible-${doc.id}`" v-model="doc.is_available"
                           @change="toggleAvailability(doc)">
                    <label class="form-check-label small" :for="`visible-${doc.id}`">
                      Visible to students
                    </label>
                  </div>
                  <div class="btn-group btn-group-sm" role="group">
                    <button class="btn btn-outline-primary" @click="viewDocument(doc)">
                      <i class="bi bi-eye"></i> View
                    </button>
                    <button class="btn btn-outline-success" @click="downloadDocument(doc)">
                      <i class="bi bi-download"></i> Download
                    </button>
                    <button class="btn btn-outline-secondary" @click="openAssignModal(doc)">
                      <i class="bi bi-link-45deg"></i> Assign
                    </button>
                    <button class="btn btn-outline-danger" @click="deleteDocument(doc)">
                      <i class="bi bi-trash"></i> Delete
                    </button>
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Right Sidebar Slot -->
    <template v-slot:right-sidebar>
      <div class="card p-3">
        <h5 class="card-title">Quick Actions:</h5>
        <button type="button" class="btn btn-primary" :disabled="uploading"
                data-bs-toggle="modal" data-bs-target="#submitFilesModal">
          <span v-if="uploading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          <i v-else class="bi bi-upload me-2"></i>Upload Course Material
        </button>
        <p class="small text-muted mt-2 mb-0">
          PDF, MD or TXT · max 50&nbsp;MB. Uploaded to the current semester.
        </p>
      </div>
    </template>

    <SubmitFilesModal
      ref="submitFilesModal"
      :multiple="false"
      accept=".pdf,.md,.txt"
      @submitForm="uploadCourseMaterial"
    />

    <!-- Assign-to-homeworks modal -->
    <div class="modal fade" id="assignHomeworksModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Assign to homeworks</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p class="text-muted small" v-if="assigningDoc">
              Materials assigned to a homework scope the AI chat for that homework's tasks.
              This replaces the current assignment for <strong>{{ assigningDoc.file_name }}</strong>.
            </p>
            <div v-if="homeworks.length === 0" class="alert alert-info">No homeworks in this semester.</div>
            <div v-for="hw in homeworks" :key="hw.id" class="form-check">
              <input class="form-check-input" type="checkbox" :id="`assign-hw-${hw.id}`"
                     :value="hw.id" v-model="selectedHomeworkIds">
              <label class="form-check-label" :for="`assign-hw-${hw.id}`">{{ hw.title }}</label>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-primary" @click="submitAssign">Save</button>
          </div>
        </div>
      </div>
    </div>

    <StemErrorModal ref="stemErrorModal" />
  </BaseLayout>
</template>

<script>
import BaseLayout from '@/components/BaseLayout.vue';
import SubmitFilesModal from '@/components/SubmitFilesModal.vue';
import StemErrorModal from '@/components/StemErrorModal.vue';
import CourseDocumentController from '@/controllers/CourseDocumentController';
import SemesterController from '@/controllers/SemesterController';
import HomeworkController from '@/controllers/HomeworkController';
import Subtask from '@/models/subtask';
import { Modal } from 'bootstrap';
import { mapGetters } from 'vuex';

const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = ['pdf', 'md', 'txt'];
const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 20;

export default {
  name: 'CourseMaterialsView',
  components: {
    BaseLayout,
    SubmitFilesModal,
    StemErrorModal,
  },
  data() {
    return {
      courseDocuments: [],
      semesters: [],
      selectedSemesterId: null,
      loading: true,
      uploading: false,
      pollTimer: null,
      homeworks: [],
      assigningDoc: null,
      selectedHomeworkIds: [],
    };
  },
  computed: {
    ...mapGetters(['isTeacher', 'semesterId']),
    activeSemesters() {
      return this.semesters.filter((s) => !s.archived);
    },
    archivedSemesters() {
      return this.semesters.filter((s) => s.archived);
    },
  },
  beforeUnmount() {
    this.clearPoll();
  },
  async created() {
    // Check if the user is a teacher
    if (!this.isTeacher) {
      this.$store.dispatch('triggerAlert', {
        type: 'error',
        title: 'Error',
        message: 'You are not authorized to access this page.',
      });
      this.$router.push('/');
      return;
    }
    this.selectedSemesterId = this.semesterId;
    await this.loadSemesters();
    await this.loadHomeworks();
    await this.fetchDocuments();
  },
  methods: {
    async loadSemesters() {
      try {
        this.semesters = await SemesterController.fetchSemesters(true);
      } catch (error) {
        // Non-fatal: the selector just won't show semester names.
        this.semesters = [];
      }
    },
    async loadHomeworks() {
      try {
        this.homeworks = await HomeworkController.fetchHomeworks();
      } catch (error) {
        // Non-fatal: the assign modal just won't list homeworks.
        this.homeworks = [];
      }
    },
    openAssignModal(doc) {
      this.assigningDoc = doc;
      this.selectedHomeworkIds = [...(doc.homework_ids || [])];
      Modal.getOrCreateInstance(document.getElementById('assignHomeworksModal')).show();
    },
    async submitAssign() {
      try {
        await CourseDocumentController.assignCourseDocument(
          this.assigningDoc.id, this.selectedHomeworkIds);
        this.$store.dispatch('triggerAlert', {
          type: 'success',
          title: 'Success',
          message: 'Homework assignment updated.',
        });
        Modal.getOrCreateInstance(document.getElementById('assignHomeworksModal')).hide();
        await this.fetchDocuments();
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    async fetchDocuments() {
      this.loading = true;
      try {
        this.courseDocuments = await CourseDocumentController.fetchCourseDocuments(this.selectedSemesterId);
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.loading = false;
      }
    },
    // Re-fetch on an interval while any doc is still indexing in the background, so
    // the "Indexing…" badge flips to Indexed/Not-indexed without a manual refresh.
    pollIndexing(attempt = 0) {
      this.clearPoll();
      if (attempt >= MAX_POLLS) {
        return;
      }
      if (!this.courseDocuments.some((doc) => doc.isIndexing)) {
        return;
      }
      this.pollTimer = setTimeout(async () => {
        await this.fetchDocuments();
        this.pollIndexing(attempt + 1);
      }, POLL_INTERVAL_MS);
    },
    clearPoll() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async uploadCourseMaterial(files) {
      if (!files || files.length !== 1) {
        return;
      }
      const file = files[0];
      const ext = file.name.split('.').pop().toLowerCase();
      if (!ACCEPTED_EXTENSIONS.includes(ext)) {
        this.$refs.stemErrorModal.show('Error', 'Only PDF, MD or TXT files are allowed.');
        return;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        this.$refs.stemErrorModal.show('Error', 'File is too large (max 50 MB).');
        return;
      }
      try {
        this.uploading = true;
        const formData = new FormData();
        formData.append('file', file);
        await CourseDocumentController.uploadCourseDocument(formData);
        this.$store.dispatch('triggerAlert', {
          type: 'success',
          title: 'Uploaded',
          message: 'Course material uploaded; indexing in the background.',
        });
        // Uploads always land in the current semester; show it so the new doc is visible.
        this.selectedSemesterId = this.semesterId;
        await this.fetchDocuments();
        // The new doc starts as "Indexing…"; poll until the background task finishes.
        this.pollIndexing();
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.uploading = false;
      }
    },
    async viewDocument(doc) {
      try {
        const response = await CourseDocumentController.downloadCourseDocument(doc.file_path);
        window.open(URL.createObjectURL(new Blob([response.data])));
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    async downloadDocument(doc) {
      try {
        const response = await CourseDocumentController.downloadCourseDocument(doc.file_path);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', doc.file_name);
        document.body.appendChild(link);
        link.click();
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    // v-model has already flipped doc.is_available to the new value; persist it and
    // revert on failure. Publishing/hiding takes effect immediately for students.
    async toggleAvailability(doc) {
      try {
        await CourseDocumentController.setCourseDocumentAvailability(doc.id, doc.is_available);
        this.$store.dispatch('triggerAlert', {
          type: 'success',
          title: 'Success',
          message: doc.is_available
            ? `"${doc.file_name}" is now visible to students.`
            : `"${doc.file_name}" is now hidden from students.`,
        });
      } catch (error) {
        doc.is_available = !doc.is_available;
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    async deleteDocument(doc) {
      if (!window.confirm(`Delete "${doc.file_name}"? This removes it from the AI chat corpus.`)) {
        return;
      }
      try {
        await CourseDocumentController.deleteCourseDocument(doc.id);
        this.courseDocuments = this.courseDocuments.filter((d) => d.id !== doc.id);
        this.$store.dispatch('triggerAlert', {
          type: 'success',
          title: 'Success',
          message: 'Course material deleted.',
        });
        await this.fetchDocuments();
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    semesterName(semesterId) {
      const semester = this.semesters.find((s) => s.id === semesterId);
      if (!semester) {
        return `Semester #${semesterId}`;
      }
      return semester.archived ? `${semester.name} (archived)` : semester.name;
    },
    formatFileSize(bytes) {
      return Subtask.formatFileSize(bytes);
    },
    formatDate(date) {
      return Subtask.formatDateTimeZone(date);
    },
  },
};
</script>
